import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  AvailableChargerStationReport,
  RequestReportInterFace,
} from '../../../../interface/reportChargerLocation.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import {
  fetchChargerReportStation,
  updateReportFilters,
  fetchListFilters,
  setReportFiltersData,
  setChargerStationReportUpdate,
  setSortData,
} from '../../../../redux/slices/ReportChargerLocationSlice';
import { resetReportUpdateFlag } from '../../../../redux/slices/ReportSlice';
import CustomDateRangePicker, {
  calculateDateRange,
  normalizePresetKey,
} from '../../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import { useTableSidePanel } from '../../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import LocationSidePanel from '../../../../shared-components/table-side-panel/LocationSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import FormatDuration from '../../../../shared-functions/FormatDuration';
import reportApi from '../../../../utils/api/reports';

interface ReportTableProperties {
  leftTopContent?: JSX.Element;
  preferenceFilter: any;
  rightTopContent?: React.ReactElement;
}

/**
 * Reports management page
 * @param {any} properties - The properties
 */
function ChargerReportTableStation(properties: Readonly<ReportTableProperties>) {
  const { t } = useTranslation('report');
  const { leftTopContent, preferenceFilter, rightTopContent } = properties;
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const previousAccountId = useRef<string | undefined>(undefined);

  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>(() => {
    const defaultRange = calculateDateRange(normalizePresetKey('last14Days'), 'future');
    return {
      startDate: defaultRange?.startDate || '',
      endDate: defaultRange?.endDate || '',
    };
  });
  const previousDateRange = useRef<{ startDate: string; endDate: string }>({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  /**
   * Clears the date range values.
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Render the date filters.
   * @returns {React.JSX.Element} The rendered date filter elements.
   */
  const renderDateFilters = () => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
          onClear={clearDateRange}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          defaultPreset="last14Days"
          disableDates="future"
        />
      </div>
    );
  };
  const {
    reports,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
    isChargerStationReportUpdate,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.reportChargerLocation);
  const dispatch = useAppDispatch();
  const reportsData = useAppSelector((state) => state.reportData);

  /**
   * Fetches Charger report data for the selected facility.
   * @param {RequestReportInterFace} data - The request parameters for fetching the report.
   */
  const fetchData = useCallback(
    (data: RequestReportInterFace) => {
      dispatch(
        fetchChargerReportStation(
          removeEmptyKeys({
            ...data,
            accountCustomId: data.accountCustomId || reportsData?.accountId,
          }),
        ),
      );
    },
    [dispatch, reportsData?.accountId],
  );

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page,
      limit,
      sortField,
      sortBy,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  };
  const {
    isEditMode,
    handleEditModeChange,
    handleSidePanelClose,
    renderSidePanelTopLeftContent,
    handleOpenStationSidePanel,
  } = useTableSidePanel({
    onRefresh: handleRefresh,
    sessions: reports,
    disableEdit: true,
    disableDelete: true,
  });

  const column = [
    {
      label: t('sNo'),
      accessorKey: 's_no' as keyof AvailableChargerStationReport,
      /**
       * S.No view
       * @param {any} report - The report
       * @param {any} index - The index
       */
      customData: (report: AvailableChargerStationReport, index: number) => (
        <div className="table-row-each-data" style={{ alignSelf: 'center' }}>
          {index + 1}
        </div>
      ),
      columnWidth: 50,
    },
    {
      label: t('stationInfo'),
      accessorKey: 'chargingStationName' as keyof AvailableChargerStationReport,
      /**
       * S.No view
       * @param {any} report - The report
       */
      customData: (report: AvailableChargerStationReport) => (
        <div className="table-row-each-data">
          <div className="station-info-left">
            <span
              role="presentation"
              className="hyperlink"
              onClick={(event) => {
                event.stopPropagation();
                handleOpenStationSidePanel(report);
              }}>
              {report.chargingStationName}
            </span>
            <span>{report.date}</span>
          </div>
          <div className="station-info-right">
            <span role="button" tabIndex={0}>
              {report.chargingStationLocation}
            </span>
          </div>
        </div>
      ),
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStation',
      sidePanelComponent: (
        <LocationSidePanel
          fetchIdFrom="queryParams"
          initialIsEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
      sortAllow: false,
      columnWidth: 204,
    },

    {
      label: t('noOfDevice'),
      accessorKey: 'deviceCount' as keyof AvailableChargerStationReport,
      sortAllow: true,
      /**
       * S.No view
       * @param {any} report - The report
       */
      customData: (report: AvailableChargerStationReport) => (
        <div className="table-row-each-data">{report.deviceCount ?? 0}</div>
      ),
    },
    {
      label: t('uptime'),
      accessorKey: 'uptime' as keyof AvailableChargerStationReport,
      sortAllow: true,
      /**
       * S.No view
       * @param {any} report - The report
       */
      customData: (report: AvailableChargerStationReport) => (
        <div className="table-row-each-data">
          {FormatDuration(report.uptime, { formatBy: 'sec', showDays: true })}
        </div>
      ),
    },
    {
      label: t('downtime'),
      accessorKey: 'downtime' as keyof AvailableChargerStationReport,
      sortAllow: true,
      /**
       * S.No view
       * @param {any} report - The report
       */
      customData: (report: AvailableChargerStationReport) => (
        <div className="table-row-each-data">
          {FormatDuration(report.downtime, { formatBy: 'sec', showDays: true })}
        </div>
      ),
    },

    {
      label: t('idle'),
      accessorKey: 'idle' as keyof AvailableChargerStationReport,
      sortAllow: true,
      /**
       * S.No view
       * @param {any} report - The report
       */
      customData: (report: AvailableChargerStationReport) => (
        <div className="table-row-each-data">
          {FormatDuration(report.idle, { formatBy: 'sec', showDays: true })}
        </div>
      ),
    },
    {
      label: t('inUse'),
      accessorKey: 'inuse' as keyof AvailableChargerStationReport,
      sortAllow: true,
      /**
       * S.No view
       * @param {any} report - The report
       */
      customData: (report: AvailableChargerStationReport) => (
        <div className="table-row-each-data">
          {FormatDuration(report.inuse, { formatBy: 'sec', showDays: true })}
        </div>
      ),
    },
  ];

  /**
   * Current columns for Charger Station Report export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'date' },
    { label: t('stationName'), accessorKey: 'chargingStationName' },
    { label: t('stationLocation'), accessorKey: 'chargingStationLocation' },
    { label: t('noOfDevice'), accessorKey: 'deviceCount' },
    { label: t('uptime'), accessorKey: 'uptime' },
    { label: t('downtime'), accessorKey: 'downtime' },
    { label: t('idle'), accessorKey: 'idle' },
    { label: t('inUse'), accessorKey: 'inuse' }, // backend key
  ];

  const exportCurrentColumns = currentColumns.filter((col) => {
    // Always show serial number
    if (col.accessorKey === '__serial__') return true;

    // 🔥 If chargingStationName is hidden,
    // automatically hide related columns
    const isParentHidden = hiddenColumns.includes('chargingStationName');

    if (isParentHidden) {
      if (
        col.accessorKey === 'chargingStationName' ||
        col.accessorKey === 'date' ||
        col.accessorKey === 'chargingStationLocation'
      ) {
        return false;
      }
    }

    // Default hide logic
    return !hiddenColumns.includes(col.accessorKey);
  });

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);

    const exportParameters: any = {
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    };

    if (sortBy) {
      exportParameters.sortBy = sortBy;
      exportParameters.sortField = sortField;
    }

    reportApi
      .fetchChargerStationListExport(
        removeEmptyKeys({
          ...exportParameters,
          accountCustomId: reportsData?.accountId,
        }),
      )
      .then((response) => {
        const headers = exportCurrentColumns.map((col) => col.label);

        const rows = response.data.map((row: AvailableChargerStationReport, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'date':
                return row?.date || '-';

              case 'chargingStationName':
                return row?.chargingStationName || '-';

              case 'chargingStationLocation':
                return row?.chargingStationLocation || '-';

              case 'deviceCount':
                return row?.deviceCount ?? '0';

              case 'uptime':
                return FormatDuration(row?.uptime, { formatBy: 'sec', showDays: true });

              case 'downtime':
                return FormatDuration(row?.downtime, { formatBy: 'sec', showDays: true });

              case 'idle':
                return FormatDuration(row?.idle, { formatBy: 'sec', showDays: true });

              case 'inuse':
                return FormatDuration(row?.inuse, { formatBy: 'sec', showDays: true });

              default:
                return '-';
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'reports-list.csv');
          } else {
            exportPDF(headers, rows, 'reports-list.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Reference Current Pages
   */
  const ReferenceCurrentPages = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortBy,
      sortField,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      page,
      limit,
    });
  };

  /**
   * Function to handle ApiRequest when page or limit changes
   * @param {any} value - The request parameters
   */
  const handleApiRequest = (value: RequestReportInterFace) => {
    fetchData({
      ...GetParametersFromCommonFilters(filters),
      ...value,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      sortField,
      sortBy,
    });
  };
  /**
   * Fetches charger station report data with query parameters.
   * @param {string} [customAccountId] - The account custom ID.
   * @param {boolean} [resetFilters] - Whether to reset filters.
   */
  const fetchReportData = useCallback(
    (customAccountId?: string, resetFilters = false) => {
      const accountCustomId = customAccountId || reportsData?.accountId;
      const queryParameters = resetFilters ? {} : GetParametersFromCommonFilters(filters);
      if (accountCustomId) {
        fetchData({
          ...queryParameters,
          accountCustomId,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          page: DefaultApiLimit.page,
          limit: DefaultApiLimit.limit,
        });
      }
    },
    [filters, dateRange.startDate, dateRange.endDate, reportsData?.accountId, fetchData],
  );

  useEffect(() => {
    const isDateRangeChanged =
      previousDateRange.current.startDate !== dateRange.startDate ||
      previousDateRange.current.endDate !== dateRange.endDate;

    if (
      isFilterUpdated ||
      isChargerStationReportUpdate ||
      reportsData.isReportUpdated ||
      isDateRangeChanged
    ) {
      if (isDateRangeChanged) {
        previousDateRange.current = dateRange;
      }
      fetchReportData();
      if (reportsData.isReportUpdated) {
        dispatch(resetReportUpdateFlag());
      }
      if (isChargerStationReportUpdate) {
        dispatch(setChargerStationReportUpdate(false));
      }
    }
  }, [
    fetchReportData,
    isFilterUpdated,
    isChargerStationReportUpdate,
    reportsData.isReportUpdated,
    dateRange.startDate,
    dateRange.endDate,
    dispatch,
  ]);

  useEffect(() => {
    if (
      preferenceFilter.pageName === 'chargerUtilizationReport' &&
      filters.length > 1 &&
      reportsData.reportByType === 'station'
    ) {
      const setFilter = filters.map((filter: any) => {
        let returnValue = filter;
        const single = preferenceFilter.filter?.find(
          (preference: any) => preference.field === filter.key,
        );
        if (single && single?.value.length > 0) {
          returnValue = {
            ...returnValue,
            value: single?.value,
            applyFilter: true,
            valueToShow: returnValue?.listOfValue
              ?.filter((show: any) => single?.value?.includes(show.value))
              .map((show: any) => show.label),
          };
        } else {
          returnValue = {
            ...returnValue,
            value: [],
            applyFilter: false,
            valueToShow: [],
          };
        }
        return returnValue;
      });
      dispatch(setReportFiltersData(setFilter));
      const queryParameters = GetParametersFromCommonFilters(setFilter);
      fetchData({
        ...queryParameters,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        page: DefaultApiLimit.page,
        limit,
      });
    }
  }, [preferenceFilter]);

  useEffect(() => {
    if (reportsData?.accountId) {
      if (previousAccountId.current !== reportsData.accountId) {
        previousAccountId.current = reportsData.accountId;
        dispatch(fetchListFilters({ accountCustomId: reportsData.accountId }));
        fetchReportData(reportsData.accountId, true);
      }
    }
  }, [reportsData?.accountId, fetchReportData, dispatch]);

  return (
    <div className="report-management-wrap">
      <div className="table-container">
        <CustomTable
          disablePagination={false}
          loading={loading}
          limit={limit}
          header={column}
          data={reports}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          pageLimitChange={handleApiRequest}
          version="v2"
          height={370}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportData={reports}
          exportLoader={exportLoader}
          disableHeader={false}
          filters={filters}
          filtersToDispatch={updateReportFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
          leftTopContent={leftTopContent}
          refershCurrentPages={ReferenceCurrentPages}
          sizeChangeButtonRequired
          sortData={{ sortBy, sortField }}
          onHiddenColumnsChange={setHiddenColumns}
          handleSortChange={handleSortFunction}
          onSidePanelClose={() => {
            handleSidePanelClose();
          }}
          sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
          rightTopContent={
            <div className="table-bottom-button-list">
              {renderDateFilters()}
              <div className="right-action">{rightTopContent}</div>
            </div>
          }
          onClearFilterChange={() => {
            clearDateRange();
          }}
        />
      </div>
    </div>
  );
}

export default ChargerReportTableStation;
